import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { Injectable, NgZone, inject } from '@angular/core';
import { TokenService } from './token.service';
import { UrlService } from './url.service';
import {
  catchError,
  Observable,
  switchMap,
  takeWhile,
  throwError,
  timer,
} from 'rxjs';
import { SKIP_GLOBAL_LOADER } from '../core/interceptors/loader.tokens';

interface SignResult {
  status: 'PENDING' | 'SIGNED' | 'ERROR';
  pdfBase64?: string;
  error?: string;
}

@Injectable({
  providedIn: 'root',
})
export class WlConsensiBeService {
  private http = inject(HttpClient);
  private tokenService = inject(TokenService);
  private urlService = inject(UrlService);
  private zone = inject(NgZone);

  private withOrganizationHeader(organizationId: string): HttpHeaders {
    return new HttpHeaders({
      'x-organization-id': organizationId,
    });
  }

  createPDFModel(body: any) {
    const ctx = new HttpContext().set(SKIP_GLOBAL_LOADER, true);
    return this.http.post(
      `${this.urlService.getBEUrl()}/api/documents/templates`,
      body,
      {
        headers: {
          Authorization:
            'Basic ' +
            btoa(
              `${this.tokenService.getCredentials().username}:${
                this.tokenService.getCredentials().password
              }`,
            ),
        },
        context: ctx,
      },
    );
  }

  start(body: {
    documentId: string;
    file: { filename: string; base64: string };
    codiceDispositivo?: string;
    codiceApplicativo?: string;
    customMetadata?: any;
    gestore: string;
    username: string;
    pin: string;
  }, organizationId: string) {
    const ctx = new HttpContext().set(SKIP_GLOBAL_LOADER, true);

    return this.http.post<{
      ok: boolean;
      idRiferimento: string;
      streamUrl: string;
    }>(`${this.urlService.getBEUrl()}/api/sign/start`, body, {
      headers: this.withOrganizationHeader(organizationId),
      context: ctx,
    });
  }

  digitalSign(body: {
    file: { filename: string; base64: string };
    gestore: string;
    username: string;
    pin: string;
    otp: string;
    documentId: string;
  }, organizationId: string) {
    const ctx = new HttpContext().set(SKIP_GLOBAL_LOADER, true);

    return this.http.post(
      `${this.urlService.getBEUrl()}/api/sign/digital-sign`,
      body,
      {
        headers: this.withOrganizationHeader(organizationId),
        context: ctx,
      },
    );
  }

  streamResult$(
    id: string,
    sseUrl = `${this.urlService.getBEUrl()}/api/sign/stream/${id}`,
  ): Observable<SignResult> {
    return new Observable<SignResult>((subscriber) => {
      // NOTE: EventSource cannot set custom headers; see auth notes below
      const es = new EventSource(sseUrl);

      es.onmessage = (ev) => {
        const data: SignResult = JSON.parse(ev.data);
        // run inside Angular zone so UI updates
        this.zone.run(() => subscriber.next(data));
        if (data.status === 'SIGNED' || data.status === 'ERROR') {
          this.zone.run(() => subscriber.complete());
          es.close();
        }
      };
      es.onerror = (err) => {
        // network or server closed; surface as error so caller can fallback to polling
        this.zone.run(() => subscriber.error(err));
        es.close();
      };
      return () => es.close();
    });
  }

  /** Poll every `ms` until SIGNED/ERROR. Emits the final result and completes. */
  pollResult$(id: string, ms = 1000): Observable<SignResult> {
    const ctx = new HttpContext().set(SKIP_GLOBAL_LOADER, true);
    return timer(0, ms).pipe(
      switchMap(() =>
        this.http.get<SignResult>(
          `${this.urlService.getBEUrl()}/api/sign/result/${id}`,
          { context: ctx },
        ),
      ),
      takeWhile((r) => r.status === 'PENDING', true), // include last emission
      catchError((err) => throwError(() => err)),
    );
  }

  // wl-consensi-be.service.ts
  streamResultFromUrl$(streamUrl: string) {
    return new Observable<{
      status: string;
      pdfBase64?: string;
      error?: string;
    }>((subscriber) => {
      const es = new EventSource(streamUrl, { withCredentials: false }); // token already in URL

      es.addEventListener('message', (evt: MessageEvent) => {
        try {
          subscriber.next(JSON.parse(evt.data));
        } catch {
          // ignore malformed messages
        }
      });

      es.addEventListener('error', () => {
        es.close();
        subscriber.error(new Error('SSE failed'));
      });

      // optional: custom events like 'ready'
      es.addEventListener('ready', () => {
        /* no-op */
      });

      return () => es.close();
    });
  }

  getTablets(structureCode: string, areaCode: string, organizationId: string) {
    const ctx = new HttpContext().set(SKIP_GLOBAL_LOADER, true);
    return this.http.get<any>(
      `${this.urlService.getBEUrl()}/api/sign/tablets?codiceStruttura=${structureCode}&codiceUnitaOperativa=${areaCode}`,
      {
        headers: this.withOrganizationHeader(organizationId),
        context: ctx,
      },
    );
  }

  getDocument(documentId: string) {
    const ctx = new HttpContext().set(SKIP_GLOBAL_LOADER, true);
    return this.http.get<any>(
      `${this.urlService.getBEUrl()}/api/documents/${documentId}`,
      { context: ctx },
    );
  }

  deleteDocument(documentId: string) {
    const ctx = new HttpContext().set(SKIP_GLOBAL_LOADER, true);
    return this.http.delete<any>(
      `${this.urlService.getBEUrl()}/api/documents/${documentId}`,
      { context: ctx },
    );
  }
}
